import React, { useEffect, useRef, useState } from 'react';
import { GetStaticPropsContext } from "next";
import { getAllPricingFeatureCategories, getPricing } from "queries/Pricing";
import Container from "components/core/Container";
import 'rc-slider/assets/index.css';
import CompaniesList, { ICompaniesList } from "blocks/companies/CompaniesList";
import { ITextBlock } from "blocks/text/TextBlock";
import TextBlockContent from "blocks/text/TextBlockContent";
import Plan, { IPricingPlan } from "components/pricing/Plan";
import { IFeature } from "components/pricing/Features/Features";
import CtaBlock, { ICtaBlock } from "blocks/cta/CtaBlock";
import { withAppLayout } from "components/core/withAppLayout";
import { createSubscription } from "components/hooks/usePreview";
import { request } from "components/hooks/useQuery";
import useRouter from "components/hooks/useRouter";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import nextI18NextConfig from "../../../next-i18next.config";
import cn from "classnames";
import Faq, { IFaq } from "../../blocks/faq/Faq";
import CurrencySwitcher from "components/base/CurrencySwitcher";
import useTrans from "components/hooks/useTrans";
import Calculator_v2 from 'components/pricing/calculator/Calculator_v2';
import { useSearchParams } from "next/navigation";

export async function getStaticProps(context: GetStaticPropsContext) {
    const query = {
        query: getPricing(context.locale),
        preview: context.preview,
        withAppData: true,
        locale: context.locale
    };

    const data = await createSubscription(context, query);

    return {
        props: {
            data: data,
            seo: data.initialData.pricing.seo,
            appData: {
                ...data.initialData.header,
                ...data.initialData.footer,
            },
            ...(await serverSideTranslations(context.locale, ['common'], nextI18NextConfig)),
        }
    };
}

export type IPricingTier = {
    id: string,
    name: string,
    onboardingFee: string,
    tiers: {
        quantity: number,
        price: number,
    }[];
    fullTiers: {
        quantity: number,
        price: number,
    }[];
};

type Props = {
    pricing: {
        hero: ITextBlock[],
        logos: ICompaniesList[],
        ctaAgency: ICtaBlock[],
        callToAction: ICtaBlock,
        agencyHero: ITextBlock[],
        faq: IFaq[]
    },
    allPricingPlans: IPricingPlan[],
    allPricingTiers: IPricingTier[],
    allPricingFeatures: IFeature[],
};

function Index(props: Props) {
    const { locale } = useRouter();
    const { push, query } = useRouter();
    const searchParams = useSearchParams();
    const calculator = searchParams.get('calculator');
    const { t } = useTrans();
    const featuresRef = useRef(null);
    const calculatorRef = useRef(null);
    const hero = props.pricing.hero[0];
    const agencyHero = props.pricing.agencyHero[0];
    const logos = props.pricing.logos[0];
    const callToAction = props.pricing.callToAction;
    const plans = props.allPricingPlans;
    const pricingPlans = props.allPricingPlans.filter((plan) => plan.page === "pricing");
    const agencyPlans = props.allPricingPlans.filter((plan) => plan.page === "agency");
    const faq = props.pricing.faq[0];
    const ctaAgency: any = props.pricing.ctaAgency[0];
    const [currency, setCurrency] = useState(locale == "en" ? "GBP" : "EUR");
    const [pending, setPending] = useState(false);
    const [featureCategories, setFeatureCategories] = useState([]);

    useEffect(() => {
        setPending(true);
        request({
            query: getAllPricingFeatureCategories(locale)
        }).then((data) => {
            setFeatureCategories(data.allPricingFeatureCategories);
        })
            .catch((err) => console.log(err))
            .finally(() => {
                setPending(false);
            });
    }, []);

    const previousPlan = (currentIndex: number) => {
        if (currentIndex == 0) {
            return null;
        }

        return plans[currentIndex - 1];
    };

    const planContainerCn = cn(
        "grid gap-y-5",
        "md:grid-cols-2 md:grid-rows-2 md:gap-x-4 md:gap-y-4",
        "xl:flex xl:justify-center xl:gap-x-0 xl:flex-shrink",
    );

    const handleScrollToView = (element: any) => {
        window.scrollTo({
            behavior: "smooth",
            top: element.offsetTop - 64
        });
    };
    return (
        <>
            <div className={"bg-primary-25"}>
                <Container>
                    <section className={"pt-28 pb-10 gap-x-32"}>
                        <TextBlockContent {...hero} />
                    </section>
                    <section className={"flex justify-end mb-2 lg:mb-0"}>
                        <CurrencySwitcher currency={currency} setCurrency={setCurrency}/>
                    </section>
                    <section className={planContainerCn}>
                        {pricingPlans.map((plan, i) => (
                            <Plan
                                key={plan.id}
                                {...plan}
                                onChoosePlan={() => handleScrollToView(calculatorRef.current)}
                                onCompareFeatures={() => handleScrollToView(featuresRef.current)}
                                previousPlan={previousPlan(i)}
                                index={i}
                                currency={currency}
                            />
                        ))}
                    </section>

                    <section>
                        <CtaBlock {...ctaAgency.cta} />
                    </section>

                    <section className={"mb-14 lg:mb-20"}>
                        <CompaniesList theme={"default"} {...logos} />
                    </section>

                    {calculator && (
                        <section ref={calculatorRef}>
                            <Calculator_v2 currency={currency} setCurrency={setCurrency}/>
                        </section>
                    )}
                </Container>
            </div>
            <div className={"bg-white"} ref={featuresRef}>
                <div className={"bg-primary-25"} id={"ecommerce-pricing"}>
                    <Container>
                        <section className={"pt-20 pb-0"}>
                            <TextBlockContent {...agencyHero} />
                        </section>
                        <section
                            className={"grid gap-y-5 md:grid-cols-2 md:grid-rows-2 md:gap-x-4 md:gap-y-4 xl:flex xl:justify-center xl:gap-4 xl:flex-shrink"}
                        >
                            {agencyPlans.map((plan, i) => (
                                <Plan
                                    key={plan.id}
                                    {...plan}
                                    onChoosePlan={() => push("/pricing/service-suite")}
                                    choosePlanLabel={t('learnMore')}
                                    onCompareFeatures={null}
                                    previousPlan={previousPlan(i)}
                                    index={i}
                                    currency={currency}
                                />
                            ))}
                        </section>
                    </Container>
                </div>
                <section>
                    <CtaBlock {...callToAction} />
                </section>
                <section>
                    <Faq {...faq} />
                </section>
            </div>
        </>
    );
}

export default withAppLayout(Index);